import {
  DataGridBox,
  DeleteDialog,
  CustomToolbar,
  ZebraDataGrid,
  CrudActionButton,
  PermissionHandler,
  CustomNoRowsOverlay,
} from 'components';
import apiService from 'library/api-service';
import { ActivityType } from 'library/types';
import { Grid2 as Grid } from '@mui/material';
import { renderDataGridLocaleText } from 'utils';
import { useSingleRowSelectionGuard } from 'hooks';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import { CreateActivityTypeDialog, UpdateActivityTypeDialog } from './components';

const ActivityTypes = () => {
  // other hooks
  const intl = useIntl();
  const requireSingleRowSelection = useSingleRowSelectionGuard();

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [openDeleteActivityType, setOpenDeleteActivityType] = useState(false);
  const [openCreateActivityType, setOpenCreateActivityType] = useState(false);
  const [openUpdateActivityType, setOpenUpdateActivityType] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  // dynamic variables
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: intl.formatMessage({ id: 'activityTypes.title', defaultMessage: 'Adı' }),
        flex: 1,
      },
      {
        field: 'roles',
        headerName: intl.formatMessage({ id: 'activityTypes.roles', defaultMessage: 'Roller' }),
        flex: 1,
        valueGetter: (value: { id: number; title: string }[]) => (value ?? []).map((role) => role.title).join(', '),
      },
      {
        field: 'isEffortActivity',
        headerName: intl.formatMessage({ id: 'activityTypes.isEffortActivity', defaultMessage: 'Efor Aktivitesi mi?' }),
        type: 'boolean',
        flex: 1,
      },
      {
        field: 'isPermissionActivity',
        headerName: intl.formatMessage({
          id: 'activityTypes.isPermissionActivity',
          defaultMessage: 'İzin Aktivitesi mi?',
        }),
        type: 'boolean',
        flex: 1,
      },
    ],
    [intl]
  );

  // async helper functions
  const getActivityTypes = async () => {
    try {
      setLoading(true);

      const response = await apiService.getActivityTypes();

      if (response.isSuccess) {
        setActivityTypes(response.data);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    getActivityTypes();
  };

  // sync helper functions
  const handleCloseDeleteActivityType = () => setOpenDeleteActivityType(false);
  const handleClickOpenDeleteActivityType = () => {
    if (requireSingleRowSelection(rowSelectionModel) === null) return;

    // If the check has been passed successfully...
    setOpenDeleteActivityType(true);
  };

  const handleCloseCreateActivityType = () => setOpenCreateActivityType(false);
  const handleClickOpenCreateActivityType = () => setOpenCreateActivityType(true);

  const handleCloseUpdateActivityType = () => setOpenUpdateActivityType(false);
  const handleClickOpenUpdateActivityType = () => {
    if (requireSingleRowSelection(rowSelectionModel) === null) return;

    // If the check has been passed successfully...
    setOpenUpdateActivityType(true);
  };

  // useEffect hooks
  useEffect(() => {
    getActivityTypes();
  }, []);

  return (
    <>
      <Grid container justifyContent="flex-start" spacing={1}>
        <PermissionHandler requiredPermissionCodes={['add-activity-type']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <CrudActionButton action="create" onClick={handleClickOpenCreateActivityType}>
              <FormattedMessage id="activityTypes.createActivityType" defaultMessage="Aktivite Türü Oluştur" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['update-activity-type']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <CrudActionButton action="update" onClick={handleClickOpenUpdateActivityType}>
              <FormattedMessage id="activityTypes.updateActivityType" defaultMessage="Aktivite Türü Düzenle" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['delete-activity-type']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <CrudActionButton action="delete" onClick={handleClickOpenDeleteActivityType}>
              <FormattedMessage id="activityTypes.deleteActivityType" defaultMessage="Aktivite Türü Sil" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
      </Grid>
      <CreateActivityTypeDialog
        open={openCreateActivityType}
        handleClose={handleCloseCreateActivityType}
        refresh={refresh}
      />
      <UpdateActivityTypeDialog
        open={openUpdateActivityType}
        activityTypeId={rowSelectionModel[0]}
        handleClose={handleCloseUpdateActivityType}
        refresh={refresh}
        setRowSelectionModel={setRowSelectionModel}
      />
      <DeleteDialog
        open={openDeleteActivityType}
        id={rowSelectionModel[0]}
        handleClose={handleCloseDeleteActivityType}
        refresh={refresh}
        setRowSelectionModel={setRowSelectionModel}
        title={<FormattedMessage id="activityTypes.deleteActivityType" defaultMessage="Aktivite Türü Sil" />}
        subTitle={
          <FormattedMessage
            id="activityTypes.deleteActivityTypeRepeat"
            defaultMessage="Bu işlem onaylandıktan sonra aktivite türü veritabanından silinecektir. Silme işlemini onaylıyor musunuz?"
          />
        }
        action={apiService.deleteActivityType}
        successMessage={
          <FormattedMessage
            id="activityTypes.deleteActivityTypeCompleted"
            defaultMessage="Seçili aktivite türünü silme işlemi başarıyla tamamlandı."
          />
        }
      />
      <br />
      <DataGridBox>
        <ZebraDataGrid
          /* Pagination Settings */
          pagination
          /* Chekbox Settings */
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          /* Remain Settings */
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading}
          localeText={renderDataGridLocaleText(intl)}
          rows={activityTypes}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
          }}
          disableAggregation
          disableRowGrouping
        />
      </DataGridBox>
    </>
  );
};

export default ActivityTypes;
